import { useState, useEffect } from "react";
import "./TermsAndConditions.scss";
import IconButton from "../../../components/IconButton/IconButton";
import { toast } from "react-toastify";
import GeneralTable from "../../../components/Table/GeneralTable";
import axiosInstance from "../../../axiosInstance";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";

const TermsAndConditions = ({ markPageAsCompleted }) => {
  const [info, setInfo] = useState("");
  const [items, setItems] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [type, setType] = useState("Terms");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [bulkUpdateEnabled, setBulkUpdateEnabled] = useState(false);
  const [editableItems, setEditableItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!info.trim()) {
      newErrors.info = "Info is required.";
    }
    if (!type) {
      newErrors.type = "Please select a type.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    const fetchTermsAndConditions = async () => {
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_BASE_URL}/terms-and-conditions/terms-and-conditions`,
        );
        const data = response.data?.data || [];

        if (data.length > 0) {
          setItems(data);
          setEditableItems(data.map((item) => ({ ...item, isEditing: false })));
          markPageAsCompleted("Terms & Conditions");
        } else {
          console.error(
            "No terms and conditions found or empty data:",
            response.data,
          );
          setItems([]);
          setEditableItems([]);
        }
      } catch (error) {
        setItems([]);
        setEditableItems([]);
      }
    };

    fetchTermsAndConditions();
  }, [markPageAsCompleted]); // FIX: Added missing dependency

  const handleAdd = async () => {
    setLoading(true);
    if (!validateForm()) {
      toast.error("Please fix the errors before submitting.");
      setLoading(false);
      return;
    }

    const newItem = {
      type,
      info,
      companyId: parseInt(process.env.REACT_APP_COMPANY_ID),
    };

    try {
      if (editingIndex !== null) {
        const itemToUpdate = items[editingIndex];
        await axiosInstance.put(
          `${process.env.REACT_APP_API_BASE_URL}/terms-and-conditions`,
          {
            ...newItem,
            termsAndConditionId: itemToUpdate.termsAndConditionId,
          },
        );

        const updatedItems = items.map((item, index) =>
          index === editingIndex ? { ...itemToUpdate, ...newItem } : item,
        );
        setItems(updatedItems);
        setEditableItems(updatedItems);
        setEditingIndex(null);
        toast.success("Terms and Conditions updated successfully!");
      } else {
        const response = await axiosInstance.post(
          `${process.env.REACT_APP_API_BASE_URL}/terms-and-conditions`,
          newItem,
        );

        const createdItem = response.data.data;
        setItems([...items, createdItem]);
        setEditableItems([...editableItems, createdItem]);
        toast.success("Terms and Conditions created successfully!");
      }

      setInfo("");
      setType("Terms");
      setShowCreateForm(false);
      if (markPageAsCompleted) {
        markPageAsCompleted("Terms & Conditions");
      }
    } catch (error) {
      console.error(
        "Error adding/updating Terms and Conditions:",
        error.response ? error.response.data : error.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (index) => {
    const itemToDelete = items[index];
    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_BASE_URL}/terms-and-conditions/${itemToDelete.termsAndConditionId}`,
      );
      const newItems = [...items];
      newItems.splice(index, 1);
      setItems(newItems);
      setEditableItems(newItems);
      toast.success("Terms and Conditions deleted successfully!");
    } catch (error) {
      console.log(error);
    }
  };

  const handleEdit = (index) => {
    const itemToEdit = items[index];
    setInfo(itemToEdit.info);
    setType(itemToEdit.type);
    setEditingIndex(index);
    setShowCreateForm(true);
  };

  const columns = [
    { header: "Info", accessor: "info" },
    { header: "Type", accessor: "type" },
    { header: "Action", accessor: "action" },
  ];

  const handleBulkUpdateClick = () => {
    if (bulkUpdateEnabled) {
      handleBulkUpdate();
    } else {
      setBulkUpdateEnabled((prev) => !prev);
    }
  };

  const handleBulkUpdate = async () => {
    try {
      const updatedItems = editableItems.map((item) => ({
        termsAndConditionId: item.termsAndConditionId,
        type: item.type,
        info: item.info,
        companyId: parseInt(process.env.REACT_APP_COMPANY_ID),
      }));

      await axiosInstance.put(
        `${process.env.REACT_APP_API_BASE_URL}/terms-and-conditions/batch`,
        updatedItems,
      );
      setItems(updatedItems);
      toast.success("Terms and Conditions updated successfully in bulk!");
      setBulkUpdateEnabled(false);
    } catch (error) {
      console.error("Error during bulk update:", error);
    }
  };

  const tableData = items.map((item, index) => ({
    info: bulkUpdateEnabled ? (
      <textarea
        value={editableItems[index]?.info || ""}
        onChange={(e) => {
          const updatedItems = [...editableItems];
          updatedItems[index].info = e.target.value;
          setEditableItems(updatedItems);
        }}
        rows={3}
        className="info-textarea"
      />
    ) : (
      item.info
    ),
    type: bulkUpdateEnabled ? (
      <div className="type-selection bulk-type-selection">
        <button
          className={`type-button ${
            editableItems[index]?.type === "Terms" ? "selected" : ""
          }`}
          onClick={() => {
            const updatedItems = [...editableItems];
            updatedItems[index].type = "Terms";
            setEditableItems(updatedItems);
          }}
        >
          Terms
        </button>
        <button
          className={`type-button ${
            editableItems[index]?.type === "Condition" ? "selected" : ""
          }`}
          onClick={() => {
            const updatedItems = [...editableItems];
            updatedItems[index].type = "Condition";
            setEditableItems(updatedItems);
          }}
        >
          Condition
        </button>
      </div>
    ) : (
      <span className={`type-badge ${item.type.toLowerCase()}`}>
        {item.type}
      </span>
    ),
    action: (
      <div className="terms-action-buttons">
        <IconButton
          icon="iconamoon:edit-light"
          onClick={() => handleEdit(index)}
          variant="edit"
          iconName="edit"
          title="Edit T&C"
        />
        <IconButton
          icon="fluent:delete-28-regular"
          onClick={() => handleDelete(index)}
          variant="delete"
          iconName="delete"
          title="Delete T&C"
        />
      </div>
    ),
  }));

  return (
    <div className="terms-and-conditions-container">
      <div className="header-section">
        <h2 className="display6">Terms & Conditions</h2>
        <div className="action-buttons-header">
          <button
            className="create-role-button"
            onClick={() => {
              setShowCreateForm(!showCreateForm);
              setInfo("");
              setType("Terms");
              setEditingIndex(null);
              setErrors({});
            }}
            style={
              showCreateForm
                ? { backgroundColor: "#ff6692", color: "#fff" }
                : {}
            }
          >
            {!showCreateForm && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="icon"
                width="16"
                height="16"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 4v16m8-8H4"
                />
              </svg>
            )}
            {showCreateForm
              ? "Close Terms & Conditions Form"
              : "Create Terms & Conditions"}
          </button>

          <button
            className="create-role-button"
            onClick={handleBulkUpdateClick}
            style={
              bulkUpdateEnabled
                ? { backgroundColor: "#2b9b39", color: "#fff" }
                : {}
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="icon"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 6h16M4 12h16m-7 6h7"
              />
            </svg>

            {bulkUpdateEnabled ? (
              <span>
                <span className="submit-text">Submit</span>{" "}
                <span className="bulk-update-text">Bulk Update</span>
              </span>
            ) : (
              <span>
                <span className="enable-text">Enable</span>{" "}
                <span className="bulk-update-text">Bulk Update</span>
              </span>
            )}
          </button>
        </div>
      </div>

      {showCreateForm && (
        <div className="w-full p-3 bg-white rounded-2xl shadow-lg border border-gray-100 space-y-10">
          <div className="form-card">
            <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-2">
              {editingIndex !== null
                ? "Edit Term/Condition"
                : "Create New Term/Condition"}
            </h3>

            <div className="form-group">
              <label className="form-label">Type</label>
              <div className="type-selection">
                <button
                  className={`type-button ${
                    type === "Terms" ? "selected" : ""
                  }`}
                  onClick={() => setType("Terms")}
                  type="button"
                >
                  <span className="type-icon">📋</span>
                  Terms
                </button>
                <button
                  className={`type-button ${
                    type === "Condition" ? "selected" : ""
                  }`}
                  onClick={() => setType("Condition")}
                  type="button"
                >
                  <span className="type-icon">⚖️</span>
                  Condition
                </button>
              </div>
              {errors.type && (
                <div className="error-message">{errors.type}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Content</label>
              <textarea
                className="content-input"
                value={info}
                onChange={(e) => setInfo(e.target.value)}
                placeholder="Enter the terms or condition content..."
                rows={4}
              />
              {errors.info && (
                <div className="error-message">{errors.info}</div>
              )}
            </div>

            <div className="form-actions">
              <ButtonWithIcon
                type="button"
                icon="tdesign:send"
                text={editingIndex !== null ? "Update" : "Create"}
                onClick={handleAdd}
                disabled={loading}
                className="submit-button"
              />
            </div>
          </div>
        </div>
      )}

      <div className="table-container">
        <GeneralTable
          data={tableData}
          columns={columns}
          title="Terms and Conditions"
          initialPageSize={10}
          showPagination={true}
        />
      </div>
    </div>
  );
};

export default TermsAndConditions;
